/**
 * Abstract base class for Propelsia Web Components.
 *
 * Subclasses (e.g. `PropelsiaBookingElement`) implement:
 *  - `static observedAttributes` — to declare reactive attributes
 *  - `getRootComponent()` — returns the React element to render
 *  - `injectStyles(shadow)` — optional, to inject scoped CSS into the Shadow Root
 *
 * The base class handles:
 *  - Shadow DOM creation
 *  - React root lifecycle (mount on connect, unmount on disconnect)
 *  - Theme CSS variables (--w-*) applied on the host element
 *  - Re-render on attribute changes
 *  - Modal mode (when `mode="modal"`) — opens a Light DOM dialog on click
 */

import { createRoot, type Root } from 'react-dom/client';
import { type ReactNode } from 'react';
import { translate } from '@cfixe-monorepo/shared';
import { applyThemeToHost, THEME_ATTRIBUTE_NAMES, type ThemeAttrs } from './theme';
import { dispatchWidgetEvent, type WidgetEventDetail } from './events';
import { createModalPortal, MODAL_CONTENT_ATTRIBUTE, type ModalPortalHandle } from './modal-portal';

export type WidgetMode = 'inline' | 'modal';

/* Payment returns already claimed by a widget on this page load: the URL does not say which of
   several widgets of the same kind started the payment, so the first one takes it. */
const claimedRedirectReturns = new Set<string>();

export abstract class WidgetElement extends HTMLElement {
  protected shadow: ShadowRoot;
  protected reactRoot: Root | null = null;
  protected modalPortal: ModalPortalHandle | null = null;
  protected modalReactRoot: Root | null = null;
  protected isConnectedFlag = false;
  /**
   * Parameter of the page's Stripe return this widget claimed (see `getRedirectReturnParams`), or
   * null: only its tunnel may resume it — subclasses forward it to their React root.
   */
  protected claimedRedirectReturn: string | null = null;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
  }

  /** Subclass returns the React element to render. */
  protected abstract getRootComponent(): ReactNode;

  /**
   * Inject the widget's stylesheet into the Shadow Root. Subclasses override
   * this to load their bundled CSS (e.g. via `?inline` import).
   */
  protected injectStyles(shadow: ShadowRoot): void {
    void shadow;
    /* override in subclass to append <style> or adoptedStyleSheets */
  }

  /**
   * Element label for the "Open" button rendered in the host element when
   * `mode="modal"` is set (so the inline footprint is just a button).
   */
  protected getModalTriggerLabel(): string {
    return 'Réserver';
  }

  /** Libellé accessible du bouton « fermer » de la modale (affiché en plein écran, sur petit écran). */
  protected getModalCloseLabel(): string {
    return translate('fr-FR', 'GLOBAL', 'CLOSE') ?? '';
  }

  /**
   * Variables de thème (`--w-*`) sur l'élément et sur la modale : rendue dans `<body>`, celle-ci n'hérite
   * pas de l'élément — sans elles, le thème était ignoré en mode modal.
   */
  private applyTheme(): void {
    const attrs = this.readThemeAttrs();
    applyThemeToHost(this, attrs);
    if (this.modalPortal) applyThemeToHost(this.modalPortal.dialog, attrs);
  }

  /** Read theme attributes from this element. */
  protected readThemeAttrs(): ThemeAttrs {
    return {
      accent: this.getAttribute('accent'),
      bg: this.getAttribute('bg'),
      text: this.getAttribute('text'),
      radius: this.getAttribute('radius'),
      font: this.getAttribute('font'),
    };
  }

  protected getMode(): WidgetMode {
    const value = this.getAttribute('mode');
    return value === 'modal' ? 'modal' : 'inline';
  }

  /** This widget claimed a Stripe return of the page (see `claimedRedirectReturn`). */
  protected get ownsRedirectReturn(): boolean {
    return this.claimedRedirectReturn !== null;
  }

  /**
   * Query parameters marking a Stripe return for this widget's tunnels (`REDIRECT_RETURN_PARAM`) —
   * several when the widget hosts several tunnels (e.g. booking + ride), empty when it takes no
   * payment.
   */
  protected getRedirectReturnParams(): readonly string[] {
    return [];
  }

  /**
   * The first widget of the page — inline or modal — to see its return in the URL claims it. The
   * others must leave it alone: otherwise an inline tunnel could resume it while a modal opens
   * empty over the page. Returns true only when this call claims it: a modal opens by itself at
   * that moment (its tree only mounts on click), never again when the host re-attaches the element.
   */
  private claimRedirectReturn(): boolean {
    if (this.ownsRedirectReturn) return false;
    const search = new URLSearchParams(window.location.search);
    // A return URL carries a single tunnel's parameter (`buildRedirectReturnUrl` strips the others).
    const param = this.getRedirectReturnParams().find(
      (candidate): boolean => !claimedRedirectReturns.has(candidate) && search.has(candidate),
    );
    if (!param) return false;
    claimedRedirectReturns.add(param);
    this.claimedRedirectReturn = param;
    return true;
  }

  connectedCallback(): void {
    this.isConnectedFlag = true;
    this.applyTheme();
    this.injectStyles(this.shadow);
    const claimedNow = this.claimRedirectReturn();
    if (this.getMode() === 'modal') {
      this.renderModalTrigger();
      if (claimedNow) this.openModal();
    } else {
      this.mountReactInline();
    }
  }

  disconnectedCallback(): void {
    this.isConnectedFlag = false;
    this.unmountReact();
    this.destroyModal();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.isConnectedFlag || oldValue === newValue) return;

    if ((THEME_ATTRIBUTE_NAMES as readonly string[]).includes(name)) {
      this.applyTheme();
      return;
    }

    if (name === 'mode') {
      // Toggle between inline & modal — full remount
      this.unmountReact();
      this.destroyModal();
      this.shadow.replaceChildren();
      this.injectStyles(this.shadow);
      if (this.getMode() === 'modal') {
        this.renderModalTrigger();
      } else {
        this.mountReactInline();
      }
      return;
    }

    // Reactive React-side attribute change → re-render
    if (this.reactRoot) {
      this.reactRoot.render(this.getRootComponent());
    } else if (this.modalReactRoot) {
      this.modalReactRoot.render(this.getRootComponent());
    }
  }

  /** Convenience helper for subclasses to dispatch typed events. */
  protected emit<TDetail extends WidgetEventDetail>(eventName: string, detail: TDetail): void {
    dispatchWidgetEvent(this, eventName, detail);
  }

  // ── Inline mounting ────────────────────────────

  private mountReactInline(): void {
    if (this.reactRoot) return;
    this.reactRoot = createRoot(this.shadow);
    this.reactRoot.render(this.getRootComponent());
  }

  private unmountReact(): void {
    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }
    if (this.modalReactRoot) {
      this.modalReactRoot.unmount();
      this.modalReactRoot = null;
    }
  }

  // ── Modal mounting ─────────────────────────────

  private renderModalTrigger(): void {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = this.getModalTriggerLabel();
    button.style.cssText =
      'all:initial;display:inline-block;padding:12px 24px;background:var(--w-accent,#000);color:#fff;border-radius:var(--w-radius,8px);font-family:var(--w-font,system-ui,sans-serif);font-size:16px;font-weight:600;cursor:pointer;border:none;';
    button.addEventListener('click', (): void => this.openModal());
    this.shadow.appendChild(button);
  }

  private openModal(): void {
    if (this.modalPortal) {
      this.modalPortal.open();
      return;
    }
    this.modalPortal = createModalPortal({
      ariaLabel: this.getModalTriggerLabel(),
      closeLabel: this.getModalCloseLabel(),
      onClose: (): void => {
        // Keep the React tree mounted while modal is hidden, so users can
        // re-open without losing state. Subclasses can call `destroyModal()`
        // explicitly on completion if they want a fresh start.
      },
    });
    this.applyTheme();
    this.modalPortal.open();
    // Mount a fresh Shadow Root inside the dialog container so the React tree
    // keeps CSS isolation even though the dialog itself is Light DOM.
    const shadowHost = document.createElement('div');
    shadowHost.style.cssText = 'display:block;width:100%;height:100%;';
    shadowHost.setAttribute(MODAL_CONTENT_ATTRIBUTE, '');
    this.modalPortal.container.appendChild(shadowHost);
    const innerShadow = shadowHost.attachShadow({ mode: 'open' });
    this.injectStyles(innerShadow);
    this.modalReactRoot = createRoot(innerShadow);
    this.modalReactRoot.render(this.getRootComponent());
  }

  protected closeModal(): void {
    this.modalPortal?.close();
  }

  protected destroyModal(): void {
    if (this.modalReactRoot) {
      this.modalReactRoot.unmount();
      this.modalReactRoot = null;
    }
    if (this.modalPortal) {
      this.modalPortal.destroy();
      this.modalPortal = null;
    }
  }
}
